/**
 * SelectField component
 * @author Liwei Yeh <liwei.yeh@adasasistemas.com>
 */

// Library
import { useEffect, useState } from 'react';

// CSS Style for this module
import styles from './selectField.module.scss';

interface SelectFieldProps {
	/** Custom styling  */
	customStyles?: string;
	/** Label of the dropdown list  */
	label?: string;
	/** Select options of the dropdown list  */
	options?: string[];
	/** Defaul option of the dropdown list  */
	defaultOption?: string;
	/** Is there an error  */
	error?: boolean;
	/** Default text that is displayed within the field */
	placeholder?: string;
	/** Msg to be displayed when the status is error */
	errorMsg?: string;
	/**
	 * onChange function for keeping track field value
	 * debounce is built in
	 */
	handleChange?: (value: string) => void;
	/**
	 * preset size of the select field
	 */
	size?: 'medium' | 'auto';
	/**
	 * Boolean for determining whether this is a disabled field
	 */
	disabled?: boolean;
	/** Height of select field */
	height?: number;
	/** Element identifier selector for test */
	testId?: string;
}

/**
 * A dropdown list
 * @param {SelectFieldProps} Prop
 * @returns  A dropdown list
 */
export const SelectField = ({
	customStyles,
	label = '',
	options = [],
	defaultOption,
	error = false,
	placeholder = 'Please input a new value',
	errorMsg = 'Please fill up the field',
	testId,
	size = 'auto',
	height = 36,
	disabled,
	handleChange = () => {
		return;
	},
}: SelectFieldProps) => {
	const [opened, setOpened] = useState<boolean>(false);
	const [selected, setSelected] = useState<string | undefined>(defaultOption);
	const [clicked, setClicked] = useState<boolean>(false);
	const [heightBySize, setHeightBySize] = useState<number>(height);

	useEffect(() => {
		const closeSelect = () => {
			setOpened(false);
		};
		document.addEventListener('click', closeSelect);

		return () => {
			document.removeEventListener('click', closeSelect);
		};
	}, []);

	// Overwrite parent container height by size
	useEffect(() => {
		if (size == 'medium') {
			setHeightBySize(32);
		}
	}, [size]);

	return (
		<div className={[styles.dropdown, customStyles].join(' ')} data-testid={testId}>
			<div className={styles[`dropdown-field`]}>
				{label && (
					<label className={styles[`dropdown-name`]}>
						<p>{label}</p>
					</label>
				)}
				<div
					className={[
						styles[`dropdown-container`],
						styles[`dropdown-container-${size}`],
						!clicked &&
							styles[`dropdown-container-default`] &&
							styles[`dropdown-container-default-${size}`],
						opened && styles[`dropdown-container-opened`],
						disabled && styles[`dropdown-container-disabled`],
						error && styles['dropdown-error'],
					].join(' ')}
					style={{ height: heightBySize }}
					role={'listbox'}
					onClick={e => {
						e.stopPropagation();
						if (!disabled) {
							setOpened(prev => !prev);
						}
					}}
				>
					<div
						className={[
							styles[`dropdown-container-text`],
							disabled && styles[`dropdown-container-text-disabled`],
						].join(' ')}
						data-testid={testId + 'Input'}
					>
						{selected ? selected : defaultOption ? defaultOption : placeholder}
					</div>
				</div>
				{opened && (
					<div className={styles['dropdown-list']} data-testid={testId + 'Dropdown'}>
						{options.map((option, index) => (
							<div
								className={[
									styles['dropdown-list-item'],
									styles[`dropdown-list-item-${size}`],
									option === selected && styles['dropdown-list-item-selected'],
								].join(' ')}
								onClick={() => {
									handleChange(option);
									setSelected(option);
									setClicked(true);
								}}
								role="option"
								aria-selected={option === selected}
								key={index}
							>
								{option}
							</div>
						))}
					</div>
				)}
			</div>
			<div
				className={[
					styles['dropdown-error-text'],
					!error && styles['dropdown-error-text-hide'],
				].join(' ')}
			>
				{errorMsg}
			</div>
		</div>
	);
};
